import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { eventsAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Calendar, 
  MapPin, 
  Clock,
  Users,
  Tag
} from 'lucide-react';

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const EventCard = styled(Card)`
  padding: 0;
  overflow: hidden;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const EventContent = styled.div`
  padding: 1.5rem;
`;

const EventTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0 0 1rem 0;
  line-height: 1.4;
`;

const EventMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const EventDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin: 0 0 1rem 0;
  line-height: 1.5;
`;

const EventActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: space-between;
  align-items: center;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  
  ${props => {
    switch (props.status) {
      case 'upcoming':
        return `
          background-color: ${props.theme.colors.success + '20'};
          color: ${props.theme.colors.success};
        `;
      case 'ongoing':
        return `
          background-color: ${props.theme.colors.warning + '20'};
          color: ${props.theme.colors.warning};
        `;
      case 'completed':
        return `
          background-color: ${props.theme.colors.text.secondary + '20'};
          color: ${props.theme.colors.text.secondary};
        `;
      default:
        return `
          background-color: ${props.theme.colors.primary + '20'};
          color: ${props.theme.colors.primary};
        `;
    }
  }}
`;

const CategoryTag = styled.span`
  background-color: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const TextArea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  background-color: ${props => props.theme.colors.background.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;


const EventsManagement = () => {
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const createEmptyForm = () => ({
    title: '',
    description: '',
    event_type: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    organizer: user?.full_name || '',
    max_participants: '',
    registration_deadline: '',
    is_active: true
  });

  const [formData, setFormData] = useState(createEmptyForm);
  const [formMessage, setFormMessage] = useState('');
  const [viewingEvent, setViewingEvent] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  const resetForm = () => {
    setFormData(createEmptyForm());
    setFormMessage('');
    setEditingEvent(null);
  };
  
  const categories = [
    { value: 'meeting', label: 'Meeting' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'conference', label: 'Conference' },
    { value: 'advocacy', label: 'Advocacy' },
    { value: 'networking', label: 'Networking' },
    { value: 'other', label: 'Other' }
  ];
  
  // Fetch events from database
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsAPI.getAll();
      setEvents(response.data.data || response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };
  
  const filteredEvents = events.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' || item.event_type === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const getEventStatus = (event) => {
    if (!event.start_date || !event.end_date) return 'upcoming';
    const now = new Date();
    const startDate = new Date(`${event.start_date}T${event.start_time || '00:00'}`);
    const endDate = new Date(`${event.end_date}T${event.end_time || '23:59'}`);
    
    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'ongoing';
    return 'completed';
  };

  const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString();
  };

  const formatTime = (value, fallback = '—') => {
    if (!value) return fallback;
    return new Date(`1970-01-01T${value}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const validateSchedule = (data) => {
    if (!data.start_date || !data.end_date) {
      return 'Start date and end date are required.';
    }

    const start = new Date(`${data.start_date}T${data.start_time || '00:00'}`);
    const end = new Date(`${data.end_date}T${data.end_time || '23:59'}`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 'Please provide valid start and end dates.';
    }

    if (end < start) {
      return 'End date/time must be after the start date/time.';
    }

    return '';
  };
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormMessage('');
          setFormData(prev => ({
            ...prev,
      [name]: type === 'checkbox' ? checked : value
      }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    const scheduleError = validateSchedule(formData);
    if (scheduleError) {
      setFormMessage(scheduleError);
      return;
    }

    const payload = {
      ...formData,
      max_participants: formData.max_participants
        ? Number(formData.max_participants)
        : null,
      registration_deadline: formData.registration_deadline || null,
      organizer: formData.organizer || null,
      start_time: formData.start_time || null,
      end_time: formData.end_time || null,
    };

    try {
      setLoading(true);
      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, payload);
      } else {
        await eventsAPI.create(payload);
      }
      await fetchEvents();
      resetForm();
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving event:', err);
      setError('Failed to save event');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormMessage('');
    setFormData({
      title: event.title || '',
      description: event.description || '',
      event_type: event.event_type || '',
      start_date: event.start_date || '',
      start_time: event.start_time || '',
      end_date: event.end_date || '',
      end_time: event.end_time || '',
      location: event.location || '',
      organizer: event.organizer || '',
      max_participants: event.max_participants ?? '',
      registration_deadline: event.registration_deadline || '',
      is_active: event.is_active !== undefined ? event.is_active : true
    });
    setShowAddModal(true);
  };

  const handleView = (event) => {
    setViewingEvent(event);
    setShowViewModal(true);
  };
  
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        setLoading(true);
        await eventsAPI.delete(id);
        fetchEvents();
        if (viewingEvent?.id === id) {
          setViewingEvent(null);
          setShowViewModal(false);
        }
        setError(null);
      } catch (err) {
        console.error('Error deleting event:', err);
        setError('Failed to delete event');
      } finally {
        setLoading(false);
      }
    }
  };
  
  return (
    <EventsContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          Events Management
          <HelpTooltip
            steps={[
              {
                title: 'Step 1 – Plan events',
                content: (
                  <p>
                    Use this page to create and manage events such as workshops, conferences, and advocacy meetings.
                    You can track basic details like date, time, location, and capacity.
                  </p>
                ),
              },
              {
                title: 'Step 2 – Publish details',
                content: (
                  <ul>
                    <li>Fill in clear titles, descriptions, and contact details so participants know how to join.</li>
                    <li>Add dates, times, and maximum attendees to support planning and reporting.</li>
                  </ul>
                ),
              },
              {
                title: 'Step 3 – Maintain records',
                content: (
                  <ul>
                    <li>Edit events if plans change, or delete events that are no longer needed.</li>
                    <li>Use this list as a record of network activities over time.</li>
                  </ul>
                ),
              },
            ]}
          />
        </Title>
        {hasPermission('add_events') && (
          <Button 
            variant="primary" 
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={20} /> Add Event
          </Button>
        )}
      </Header>
      
      <SearchContainer theme={theme}>
        <Input
          placeholder="Search events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          icon={<Search size={20} />}
          style={{ minWidth: '300px' }}
        />
        <Select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category.value} value={category.value}>{category.label}</option>
          ))}
        </Select>
      </SearchContainer>
      
      <EventsGrid theme={theme}>
        {filteredEvents.map(item => {
          const status = getEventStatus(item);
          const typeLabel = categories.find(category => category.value === item.event_type)?.label || 'Event';
          return (
            <EventCard key={item.id} theme={theme}>
              <EventContent theme={theme}>
                <EventTitle theme={theme}>{item.title}</EventTitle>
                <EventMeta theme={theme}>
                  <MetaItem theme={theme}>
                    <Calendar size={16} />
                    <span>
                      {formatDate(item.start_date)}
                      {item.start_time && ` • ${formatTime(item.start_time)}`}
                    </span>
                  </MetaItem>
                  <MetaItem theme={theme}>
                    <Clock size={16} />
                    <span>
                      Ends {formatDate(item.end_date)}
                      {item.end_time && ` • ${formatTime(item.end_time)}`}
                    </span>
                  </MetaItem>
                  {item.location && (
                  <MetaItem theme={theme}>
                    <MapPin size={16} />
                    {item.location}
                  </MetaItem>
                  )}
                  {item.organizer && (
                    <MetaItem theme={theme}>
                      <Tag size={16} />
                      Organized by {item.organizer}
                    </MetaItem>
                  )}
                  {item.max_participants && (
                  <MetaItem theme={theme}>
                    <Users size={16} />
                      Max {item.max_participants} participants
                    </MetaItem>
                  )}
                  {item.registration_deadline && (
                    <MetaItem theme={theme}>
                      <Calendar size={16} />
                      Register by {formatDate(item.registration_deadline)}
                  </MetaItem>
                  )}
                </EventMeta>
                <EventDescription theme={theme}>{item.description || 'No description available.'}</EventDescription>
                <EventActions theme={theme}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <StatusBadge theme={theme} status={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </StatusBadge>
                    <CategoryTag theme={theme}>{typeLabel}</CategoryTag>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button variant="outline" size="small" onClick={() => handleView(item)}>
                      <Eye size={16} /> View
                    </Button>
                    {hasPermission('edit_events') && (
                      <Button 
                        variant="outline" 
                        size="small"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit size={16} />
                      </Button>
                    )}
                    {hasPermission('delete_events') && (
                      <Button 
                        variant="outline" 
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        style={{ color: theme.colors.error }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </EventActions>
              </EventContent>
            </EventCard>
          );
        })}
      </EventsGrid>
      
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          resetForm();
        }}
        title={editingEvent ? 'Edit Event' : 'Add Event'}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <ModalContent theme={theme}>
            {formMessage && (
              <div style={{ color: theme.colors.error }}>
                {formMessage}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Event Title</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter event title"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Event Type</Label>
                <Select
                  name="event_type"
                  value={formData.event_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Event Type</option>
                  {categories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </Select>
              </FormGroup>
            </div>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter event description"
                required
              />
            </FormGroup>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Start Date</Label>
                <Input
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Start Time</Label>
                <Input
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>End Date</Label>
                <Input
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>End Time</Label>
                <Input
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Location</Label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter location"
                  required
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Organizer</Label>
                <Input
                  name="organizer"
                  value={formData.organizer}
                  onChange={handleInputChange}
                  placeholder="Organizer name"
                />
              </FormGroup>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <FormGroup theme={theme}>
                <Label theme={theme}>Max Participants</Label>
                <Input
                  name="max_participants"
                  type="number"
                  min="1"
                  value={formData.max_participants}
                  onChange={handleInputChange}
                  placeholder="Maximum attendees"
                />
              </FormGroup>
              
              <FormGroup theme={theme}>
                <Label theme={theme}>Registration Deadline</Label>
                <Input
                  name="registration_deadline"
                  type="date"
                  value={formData.registration_deadline}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </div>
            
            <FormGroup theme={theme}>
              <Label theme={theme}>Status</Label>
              <Select
                name="is_active"
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    is_active: e.target.value === 'true'
                  }));
                }}
              >
                <option value="true">Active / Visible</option>
                <option value="false">Inactive / Hidden</option>
              </Select>
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <Button 
                type="button" 
                variant="outline"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingEvent ? 'Update' : 'Create'} Event
              </Button>
            </div>
          </ModalContent>
        </form>
      </Modal>

      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setViewingEvent(null);
        }}
        title={viewingEvent?.title || 'Event Details'}
      >
        {viewingEvent && (
          <ModalContent theme={theme}>
            <EventMeta theme={theme}>
              <MetaItem theme={theme}>
                <Calendar size={16} />
                <span>
                  {formatDate(viewingEvent.start_date)}
                  {viewingEvent.start_time && ` • ${formatTime(viewingEvent.start_time)}`}
                </span>
              </MetaItem>
              <MetaItem theme={theme}>
                <Clock size={16} />
                <span>
                  Ends {formatDate(viewingEvent.end_date)}
                  {viewingEvent.end_time && ` • ${formatTime(viewingEvent.end_time)}`}
                </span>
              </MetaItem>
              {viewingEvent.location && (
                <MetaItem theme={theme}>
                  <MapPin size={16} />
                  {viewingEvent.location}
                </MetaItem>
              )}
              {viewingEvent.organizer && (
                <MetaItem theme={theme}>
                  <Tag size={16} />
                  Organized by {viewingEvent.organizer}
                </MetaItem>
              )}
              {viewingEvent.max_participants && (
                <MetaItem theme={theme}>
                  <Users size={16} />
                  Max {viewingEvent.max_participants} participants
                </MetaItem>
              )}
              {viewingEvent.registration_deadline && (
                <MetaItem theme={theme}>
                  <Calendar size={16} />
                  Register by {formatDate(viewingEvent.registration_deadline)}
                </MetaItem>
              )}
            </EventMeta>
            <EventDescription theme={theme} style={{ whiteSpace: 'pre-line' }}>
              {viewingEvent.description || 'No description available.'}
            </EventDescription>
          </ModalContent>
        )}
      </Modal>
    </EventsContainer>
  );
};

export default EventsManagement;



